<template>
  <div>
    <div v-if="group && group._id" class="group-container d-flex align-center justify-space-between">
      <div class="d-flex align-center">
        <h2 class="mb-0">{{ group.name }}</h2>
        <div class="ml-8 input-container">
          <mf-number-input
            v-if="variant === 'trigger'"
            v-model="group.quantity"
            hide-details
            dense
            outlined
            label="Quantidade"
            :value="group.quantity"
            :min="0"
            :max="999"
            :error="validations.$each[getComboProductIndex(group)].$error && validations.$each[getComboProductIndex(group)].quantity.$invalid"
            :tooltip-text="tooltipMessage"
            :is-industry-campaign="isIndustryCampaign"
            :disabled="isDisabledInRules(rules.comboOfferSales.quantity) || isRevision || isCampaignWithOfferSale"
          />

          <v-text-field
            v-else
            v-model="group.percentage_discount"
            label="Desconto"
            outlined
            dense
            type="number"
            min="0"
            max="100"
            suffix="%"
            hide-details
            :disabled="isDisabledInRules(rules.comboOfferSales.percentageDiscount) || isRevision || isCampaignWithOfferSale"
            :error="validations.$each[getComboProductIndex(group)].$error && validations.$each[getComboProductIndex(group)].percentage_discount.$invalid"
            @keydown="$event.key === '-' || $event.key === '+' || $event.key === 'e' ? $event.preventDefault() : null"
            @blur="validateValue"
          />
        </div>
      </div>
      <mf-button
        v-if="!isRevision && !isCampaignWithOfferSale"
        type="icon"
        icon="delete"
        color="vermelho-0"
        :disabled="isIndustryCampaign || automaticCampaign"
        @click="$emit('remove-group')"
      />
    </div>
    <v-data-table
      :headers="computedHeaders"
      :items="filteredProducts"
      multi-sort
      item-key="name"
      :expanded.sync="expanded"
      :single-expand="singleExpand"
      :items-per-page="50"
      :footer-props="{ itemsPerPageOptions: [10, 20, 50] }"
      :options.sync="options"
      :class="group && group._id ? 'grouped-table' : 'ungrouped-table'"
    >
      <!-- table content -->
      <template v-slot:item="{ item, expand, isExpanded, index }">
        <tr :class="{ repeated: addErrorClass(item.sku) }">
          <td>
            <div v-if="isMultipleEans(item)">
              <mf-button type="icon" size="sm" color="marca-1" :icon="isExpanded ? 'indeterminate_check_box' : 'add_box'" @click="expand(!isExpanded)" />
            </div>
          </td>

          <td>
            {{ item.sku }}
          </td>

          <td>
            <div class="py-0">
              <span v-if="item.product && item.product.eans && item.product.eans.length > 1">
                {{ getEansCount(item) }}
              </span>
              <span v-else style="word-break: keep-all">
                {{ item.ean }}
              </span>
            </div>
          </td>

          <td>
            <field-toggle
              v-if="!item.products && !disableIfIsGrouped && !isRevision && !disabled && !isCampaignWithOfferSale"
              v-model="item.name"
              :error="(validations.$each[getComboProductIndex(item)].$error && validations.$each[getComboProductIndex(item)].name.$invalid) || item.name === ''"
              :text="true"
              :column="0"
              :row="index"
              :tooltip="isDisabledInRules(rules.comboOfferSales.name)"
              :tooltip-text="tooltipMessage"
              :is-industry-campaign="isIndustryCampaign"
              :disabled="isDisabledInRules(rules.comboOfferSales.name)"
            />
            <span v-else>{{ item.name }}</span>
          </td>

          <td v-if="!group || !group._id">
            <div v-if="variant === 'trigger'">
              <field-toggle
                v-if="!disableIfIsGrouped && !isRevision && !disabled && !isCampaignWithOfferSale"
                v-model="item.quantity"
                :error="validations.$each[getComboProductIndex(item)].$error && validations.$each[getComboProductIndex(item)].quantity.$invalid"
                :limit-value="999"
                :column="1"
                :row="index"
                :tooltip-text="tooltipMessage"
                :is-industry-campaign="isIndustryCampaign"
                :disabled="isDisabledInRules(rules.comboOfferSales.quantity)"
              />
              <span v-else>{{ item.quantity ? `${localeString(item.quantity, 0)}` : '-' }}</span>
            </div>

            <div v-else>
              <field-toggle
                v-if="!disableIfIsGrouped && !isRevision && !disabled && !isCampaignWithOfferSale"
                v-model="item.percentage_discount"
                :limit-value="100"
                :column="1"
                :row="index"
                percentage
                :tooltip-text="tooltipMessage"
                :is-industry-campaign="isIndustryCampaign"
                :error="validations.$each[getComboProductIndex(item)].$error && validations.$each[getComboProductIndex(item)].percentage_discount.$invalid"
                :disabled="isDisabledInRules(rules.comboOfferSales.percentageDiscount)"
              />
              <span v-else>{{ item.percentage_discount ? `${localeString(item.percentage_discount, 0)}%` : '-' }}</span>
            </div>
          </td>

          <td v-else-if="group && group._id && variant === 'target'">
            <span>{{ group.percentage_discount ? `${group.percentage_discount}%` : '-' }}</span>
          </td>

          <td v-if="!isRevision">
            <tooltip v-if="!isCampaignWithOfferSale" :disabled="!isIndustryCampaign" :message="tooltipMessage">
              <mf-button
                type="icon"
                icon="delete"
                color="cinza-l30"
                :disabled="isRevision || isIndustryCampaign || item.disabled || disabled || automaticCampaign"
                @click="$emit('remove-item', item)"
              />
            </tooltip>
          </td>
        </tr>
      </template>

      <template v-slot:expanded-item="{ headers, item }">
        <td class="expanded-itens" :colspan="headers.length">
          <v-checkbox
            v-for="ean in toSet(item.product.eans)"
            :key="ean"
            v-model="item.eans"
            :value="ean"
            :label="ean"
            :disabled="isCheckboxDisabled(ean, item.eans) || isRevision || disabled || isCampaignWithOfferSale"
            color="marca-1"
            multiple
          />
        </td>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import { offerSalesTables } from '../../mixins/offer-sales-tables'
export default {
  name: 'ComboTable',
  components: {
    FieldToggle: () => import('../FieldToggle.vue'),
    Tooltip: () => import('../Tooltip.vue'),
  },
  mixins: [offerSalesTables],
  props: {
    variant: {
      type: String,
      default: 'trigger',
    },
    allProducts: {
      type: Array,
      default: () => [],
    },
    isCampaignWithOfferSale: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    expanded: [],
    singleExpand: true,
  }),
  computed: {
    computedHeaders() {
      return [
        {
          text: '',
          value: 'expandAction',
          width: '2%',
          sortable: false,
        },
        {
          text: 'SKU',
          value: 'sku',
          width: '15%',
          sortable: false,
        },
        {
          text: 'EAN',
          value: 'ean',
          width: '15%',
          sortable: false,
        },
        {
          text: 'Nome',
          value: 'name',
          width: '45%',
          sortable: false,
        },
        ...(this.group && this.group._id && this.variant === 'trigger'
          ? []
          : [
              {
                text: this.variant === 'trigger' ? 'Quant.' : 'Desconto',
                value: this.variant === 'trigger' ? 'quantity' : 'discount',
                width: '15%',
                sortable: false,
              },
            ]),
        {
          text: '',
          value: 'action',
          width: '8%',
          sortable: false,
        },
      ]
    },
  },
  methods: {
    getComboProductIndex(product) {
      if (this.group && this.group._id) return this.allProducts.findIndex((item) => item._id === this.group._id)
      else return this.allProducts.findIndex((item) => item.sku === product.sku)
    },
    validateValue() {
      if (this.group.percentage_discount > 100) this.group.percentage_discount = 100
      if (this.group.percentage_discount < 0) this.group.percentage_discount = 0
    },
  },
}
</script>

<style lang="scss" scoped>
::v-deep th {
  color: $cinza-d20 !important;
  font-size: 14px;
  font-weight: bold;
}

::v-deep tbody {
  td {
    color: $cinza-0;
    font-size: 14px;
    line-height: 1;
  }
}

.invalid-input {
  color: $vermelho-0;

  .field-padding {
    span {
      border: 1px #ff5252 solid !important;
      padding: 0 5px;
    }
  }
}

.expanded-itens {
  padding-left: 19%;
}

::v-deep .repeated {
  background-color: #ffc4c4;
  &:hover {
    background-color: #ffc4c4 !important;
  }
}

::v-deep th,
::v-deep td {
  padding: 0 8px;
}

.img-container {
  width: 35px;
  height: 35px;

  & img {
    display: block;
    max-width: 100%;
    max-height: 100%;
  }
}

::v-deep .ungrouped-table {
  background: $cinza-l90;
  border-radius: 12px 12px 0 0;

  .v-data-table__wrapper {
    border-radius: 12px 12px 0 0;
  }

  thead {
    background: $cinza-l80;
  }
}

::v-deep .grouped-table {
  background: $cinza-l90;
  thead {
    background: $cinza-l80;
  }
}

.group-container {
  border-radius: 12px 12px 0 0;
  background: $cinza-l80;
  padding: 16px;

  h2 {
    font-weight: 700;
    color: $cinza-d20;
    font-size: 16px;
  }

  .input-container {
    width: 125px;
  }
}

::v-deep input::-webkit-outer-spin-button,
::v-deep input::-webkit-inner-spin-button {
  -webkit-appearance: none !important;
  margin: 0 !important;
}
::v-deep input[type='number'] {
  -moz-appearance: textfield !important;
}
</style>
